import Vue from 'vue';
import { Module } from 'vuex';
import { RootState } from '@/types/store';
import { dateParse, dateToday, dateFormat, getYear, getMonth, isUndef } from '@/lib/utils';
import { stat } from 'fs';
import { Dictionary } from 'vue-router/types/router';

export interface SignInState {
    /** 打卡数据 */
    signInData: {
        today_sign_in: { // 未报名/未打卡/报名了不在打卡期 today_sign_in为null
            image: FileId;
            image_url: string;
            time: number;
            distance: string;
            status: SignIn.Status;
        };
        total_days: number; // 未报名当前赛事，没有这个值/这个值表示在当前赛事中的打卡总天数
        total_distance: number;  // 未报名当前赛事，没有这个值/这个值表示在当前赛事中的打卡总距离
    } | null;
    /** 打卡日历数据 */
    signInMap: {
        [date: string]: SignIn.AggrDetail | undefined;
    };
    /** 是否加载过日历数据 */
    calendarCached: {
        [month: string]: boolean | undefined;
    };
    /** 打卡数据 */
    detail: {
        [id: string]: SignIn.Detail | undefined;
    };
    rejectedDetail: {
        [id: string]: SignIn.RejectedDetail | undefined;
    };
    /** 勋章id */
    badgesIds: number[] | null;
    /** 次数卡 */
    countCard: SignIn.CountCard | null;
    signInAd: Activity.Ad[];
    updateAvatarArrMap: {
      [family_avata: string]: string,


  };

    calendarCardMap: {
        [month: string]: string | undefined;
    };
    hasCalendarCardMap: {
        [month: string]: boolean | undefined;
    };
    weekData: {
        [id: number]: SignIn.WeekData | undefined;
    };
    echartsData: {
        [type: string]: SignIn.OneEchartsData[];
    };
}

const m: Module<SignInState, RootState> = {
    namespaced: true,
    state: () => ({
        signInData: null,
        signInMap: {},
        calendarCached: {},
        detail: {},
        rejectedDetail: {},
        badgesIds: null,
        countCard: null,
        signInAd: [],
        calendarCardMap: {},
        hasCalendarCardMap: {},
        weekData: {},
        echartsData: {},
        updateAvatarArrMap: {},
    }),
    mutations: {
        setSignInData(state, data) {
            state.signInData = data;
        },
        /** 填充打卡日历数据 */
        setSignIn(state, { date, signIn }: { date: string, signIn: SignIn.AggrDetail }) {
            Vue.set(state.signInMap, date, signIn);
        },
        /**
         * 记录日历是否已经加载
         */
        setCalendarCached(state, { month, cached }) {
            Vue.set(state.calendarCached, month, cached);
        },
        /**
         * 记录detail
         */
        setDetail(state, { signInId, detail }) {
            Vue.set(state.detail, signInId, detail);
        },
        /**
         * 记录不通过打卡详情
         */
        setRejectedDetail(state, { signInId, detail }) {
            Vue.set(state.rejectedDetail, signInId, detail);
        },
        /**
         * 设置点亮的勋章的ids
         */
        setBadgesIds(state, ids: number[]) {
            state.badgesIds = ids;
        },
        /**
         * 设置获得的次数卡
         */
        setCountCard(state, card: SignIn.CountCard | null) {
            state.countCard = card || null;
        },
        /** 存储打卡页广告 */
        setSignInAd(state, ads: Activity.Ad[]) {
            state.signInAd = ads;
        },
        /** 存储日历卡 */
        setCalendarCardMap(state, { month, url }: { month: string, url: string }) {
            Vue.set(state.calendarCardMap, month, url);
        },
        /** 存储日历卡 */
        setHasCalendarCardMap(state, { month, status }: { month: string, status: boolean }) {
            Vue.set(state.hasCalendarCardMap, month, status);
        },
        /** 储存周运动数据 */
        setWeekData(state, {id, weekData}) {
            Vue.set(state.weekData, id, weekData);
        },
        /** 储存周运动数据图表 */
        setEchartsData(state, {id, type, echartsData}: {id: number, type: string, echartsData: SignIn.OneEchartsData[]}) {
            Vue.set(state.echartsData,  type + id, echartsData);
        },
        updateAvatarArr(state, data) {
          state.updateAvatarArrMap = data;
        },
    },
    actions: {
        /**
         * 创建打卡
         * @param participationId 允许为空
         */
        async create({ state, commit, dispatch }, { image, time, distance, crossCountryRun }) {
            const response = await Vue.axios.post('/api/v1/participation/create_sign_in', {
                image,
                time,
                distance,
                cross_country_run: crossCountryRun,
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setBadgesIds', rdata.data.medal_ids);
                commit('setCountCard', rdata.data.count_card);
                const today = dateToday();
                const month = `${getYear(today)}-${getMonth(today) + 1}`;
                await Promise.all([
                    state.signInData ? dispatch('getSignInData', { forceUpdate: true }) : '',
                    state.calendarCached[month] ? dispatch('getCalendar', { month, forceUpdate: true }) : '',
                ]);

            }
            return rdata;
        },
        /**
         * 获取打卡数据
         */
        async getSignInData({ state, commit }, { forceUpdate } = {}) {
            if (state.signInData && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/participation/sign_in_data');
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setSignInData', rdata.data);
            }
            return rdata;
        },
        /**
         * 按月份获取打卡数据
         * @param month 格式2018-01
         */
        async getCalendar({ state, commit }, { month, forceUpdate } = {}) {
            if (state.calendarCached[month] && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/participation/sign_in_calendar', {
                params: {
                    month,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                for (const signIn of rdata.data as SignIn.AggrDetail[]) {
                    commit('setSignIn', {
                        date: signIn.date,
                        signIn,
                    });
                    commit('setCalendarCached', {
                        month,
                        cached: true,
                    });
                }
            }
            return rdata;
        },
        /**
         * 获取打卡详情
         */
        async getDetail({ state, commit }, { signInId, forceUpdate }) {
            const response = await Vue.axios.get('/api/v1/participation/sign_in_detail', {
                params: {
                    id: signInId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setDetail', {
                    signInId,
                    detail: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 获取不通过的打卡详情
         */
        async getRejectedDetail({ state, commit }, { signInId, forceUpdate }) {
            const response = await Vue.axios.get('/api/v1/participation/rejected_sign_in_detail', {
                params: {
                    sign_in_id: signInId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setRejectedDetail', {
                    signInId,
                    detail: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 获取打卡页底部广告
         */
        async getSignInAd({ state, commit }, forceUpdate) {
            if (state.signInAd && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/framework/sign_in_ads');
            const rdata = response.data;
            if (rdata.status === 0) {
                const data = rdata.data;
                commit('setSignInAd', data);
            }
            return rdata;
        },
      // 修改亲子图片
      async editImgParentAvatar({commit}, {activity_id, activity_special_id, user_id, parent_child_avatar}) {
        const response = await Vue.axios.post('/api/v1/participation/upload_parent_child_avatar', {
          activity_id,
          activity_special_id,
          user_id,
          parent_child_avatar,
        });
      },

        /**
         * 打卡申诉
         */
        async postSignInAppeal({ state, commit }, { signInId, reason }: { signInId: string, reason: string }) {
            const response = await Vue.axios.post('/api/v1/participation/sign_in_appeal', {
                sign_in_id: signInId,
                reason,
            });
            const rdata = response.data;
            return rdata;
        },

        /**
         * 获取日历卡
         */
        async getCalendarCard({ state, commit }, { month, forceUpdate = false }: { month: string, forceUpdate: boolean }) {
            if (state.calendarCardMap[month] && !forceUpdate) {
                return {
                    status: 0,
                    data: {
                        url: state.calendarCardMap[month],
                        finished: true,
                    },
                };
            }
            const response = await Vue.axios.get('/api/v1/user/calendar_card', {
                params: {
                    month,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0 && rdata.data.finished) {
                commit('setCalendarCardMap', {
                    month,
                    url: rdata.data.url,
                });
            }
            return rdata;
        },

        /**
         * 当前月份是否存在日历卡
         */
        async checkHasCalendarCard({ state, commit }, {month}: { month: string }) {
            if (!isUndef(state.hasCalendarCardMap[month])) {
                return {
                    status: 0,
                    data: state.hasCalendarCardMap[month],
                };
            }
            const response = await Vue.axios.get('/api/v1/user/month_has_card', {
                params: {
                    month,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setHasCalendarCardMap', {
                    month,
                    status: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 获得成就卡
         */
        async getCustomSignInCard({ state, commit }, { signInId, content, bg }) {
            const params: any = {
                id: signInId,
            };
            if (content) {
                params.string = content;
            }
            if (bg) {
                params.bg = bg;
            }
            const response = await Vue.axios.get('/api/v1/participation/custom_sign_in_card', {
                params,
            });
            const rdata = response.data;
            return rdata;
        },
        /**
         * 获取打卡页本周上周数据
         */
        async getWeekData({ state, commit }, { id }) {
            const response = await Vue.axios.get('/api/v1/activity_match_rank/week_data', {
                params: {
                    id,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setWeekData', {id, weekData: rdata.data});
            }
            return rdata;
        },
        /**
         * 获取打卡页本周上周数据
         */
        async getEchartsData({ state, commit }, { id, type }) {
            if (state.echartsData[type + id]) {
                return state.echartsData[type + id];
            }
            const response = await Vue.axios.get('/api/v1/activity_match_rank/echarts_data', {
                params: {
                    id,
                    type,
                },
            });
            const rdata = response.data;

            if (rdata.status === 0) {
                commit('setEchartsData', {id, type, echartsData: rdata.data});
            }
            return rdata;
        },
        /**
         * 将本次打卡数据隐藏
         */
        async deleteSignIn({ state, commit }, signInId) {
            const response = await Vue.axios.get('/api/v1/participation/delete_sign_in', {
                params: {
                    id: signInId,
                },
            });
            const rdata = response.data;
            return rdata;
        },
        //  获取用户头像
        async updateAvatar({ state, commit }, {activityId, userId, activityspecialId}) {
          const response = await Vue.axios.get( 'api/v1/participationbind_avatar', {
            params: {
              activity_id: activityId,
        user_id: userId,
        activity_special_id: activityspecialId,
            },
          });
          const rdata = response.data;
          if (rdata.status === 0) {
            console.log(rdata, '333333333333333');

            commit('updateAvatarArr', rdata.data);
          }

          return rdata;
      },
    },
};

export default m;
